import { Component, OnDestroy, OnInit } from '@angular/core';
import * as fromAttachments from '../../../store/attachments/attachments.reducer';
import * as fromProcess from '../../../store/process/process.reducer';
import * as fromRemote from '../../../store/remote/remote.reducer';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { ApiState } from '../../../model/api-state.model';
import { combineLatest, filter, first, map, Observable, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { PathPayload } from '../../../model/path.model';
import { ProcessService, ProcessType } from '../../../services/process.service';
import { AgentSaveCalculationPayload } from '../../../model/save-calculation.model';
import { FormsService } from '../../../services/forms.service';
import { TrackingService } from '../../../tracking/tracking.service';
import { PersistStorageKeys, PersistStorageService } from '../../../services/persist-storage.service';
import { RemoteKind } from '../../../model/remote-access.model';

@Component({
  selector: 'app-check-documents',
  templateUrl: './check-documents.component.html',
  styleUrls: ['./check-documents.component.scss'],
})
export class CheckDocumentsComponent implements OnInit, OnDestroy {
  attachmentsState$ = this.store.select(fromAttachments.selectAttachmentsState);
  attachmentsGroupData$ = this.store.select(fromAttachments.selectAttachmentsGroupData);
  attachmentsError$ = this.store.select(fromAttachments.selectAttachmentsError);
  processHashCode$ = this.store.select(fromProcess.selectProcessHashCode);
  remoteHashCode$ = this.store.select(fromRemote.selectHashCode);
  remoteKind$ = this.store.select(fromRemote.selectRemoteKind);
  processKind$ = combineLatest([this.store.select(fromProcess.selectProcessKind), this.store.select(fromRemote.selectPathCodeName)]).pipe(
    map(([processKind, remotePathCodeName]) => {
      if (this.isRemote) {
        return remotePathCodeName;
      }

      return processKind;
    }),
  );
  savedCalculationState$ = this.store.select(fromProcess.selectProcessSavedCalculationState);
  savedCalculationError$ = this.store.select(fromProcess.selectProcessSavedCalculationError);
  selectedPackage$ = this.store.select(fromProcess.selectProcessPackageName);
  addons$ = this.store.select(fromProcess.selectProcessAddons);
  selectedAgentSection$ = this.store.select(fromProcess.selectAgentSection);
  hashCode$ = combineLatest([this.processHashCode$, this.remoteHashCode$]).pipe(
    map(([processHashCode, remoteHashCode]) => {
      if (this.isRemote) {
        return remoteHashCode || undefined;
      }

      return processHashCode || undefined;
    }),
  );
  processPackageName$ = this.store.select(fromProcess.selectProcessPackageName);
  remotePackageName$ = this.store.select(fromRemote.selectPathCodeName);
  packageName$ = combineLatest([this.processPackageName$, this.remotePackageName$]).pipe(
    map(([processPackageName, remotePackageName]) => {
      if (this.isRemote) {
        return remotePackageName || '';
      }

      return processPackageName;
    }),
  );
  discountCode$ = this.store.select(fromProcess.selectOfferDiscountCode);
  savedCalculationPayload$ = this.store.select(fromProcess.selectProcessSavedCalculationPayload);

  ngDestroy$ = new Subject<void>();

  apiStates = ApiState;

  isRemote = this.router.url.startsWith('/remote');

  downloadAll = {
    label: 'modals.check-documents.download-all-label',
    link: 'https://www.allianz.pl',
  };

  accordionElements = [
    {
      title: 'modals.check-documents.informationClouse.title',
      description: 'modals.check-documents.informationClouse.description',
      buttons: [
        {
          label: 'modals.check-documents.informationClouse.button',
          link: 'https://www.allianz.pl',
        },
      ],
    },
    {
      title: 'modals.check-documents.productInformation.title',
      description: 'modals.check-documents.productInformation.description',
      buttons: [
        {
          label: 'modals.check-documents.productInformation.offer-button',
          link: 'https://www.allianz.pl',
        },
        {
          label: 'modals.check-documents.productInformation.letter-button',
          link: 'https://www.allianz.pl',
        },
        {
          label: 'modals.check-documents.productInformation.conditions-button',
          link: 'https://www.allianz.pl',
        },
      ],
    },
  ];

  constructor(
    private store: Store<AppState>,
    private formsService: FormsService,
    private processService: ProcessService,
    private router: Router,
    private trackingService: TrackingService,
    private persistStorageService: PersistStorageService,
  ) {}

  get remoteKindSuffix$() {
    return this.remoteKind$.pipe(map((remoteKind) => (remoteKind === RemoteKind.TravelAgency ? '-travelAgency' : '')));
  }

  get downloadAllPdf$(): Observable<string | undefined> {
    return combineLatest([this.hashCode$, this.packageName$]).pipe(
      map(([hashCode, packageName]) =>
        hashCode && packageName
          ? `${environment.baseApiUrl}globapidirect/ext/packages/AllianzTravelDirect/attachments/zip/${packageName}?hashCode=${hashCode}`
          : undefined,
      ),
      first(),
    );
  }

  ngOnInit(): void {
    if (this.isRemote) {
      this.getAttachments();

      return;
    }

    this.saveCalculation();
    this.savedCalculationState$
      .pipe(
        takeUntil(this.ngDestroy$),
        filter((state) => state === ApiState.success),
      )
      .subscribe(() => {
        this.getAttachments();
      });
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  saveCalculation() {
    combineLatest([
      this.processKind$,
      this.selectedPackage$,
      this.addons$,
      this.selectedAgentSection$,
      this.discountCode$,
      this.savedCalculationPayload$,
    ])
      .pipe(first())
      .subscribe(([processKind, packageCodeName, selectedAddons, selectedAgentSection, discountCode, savedCalculationPayload]) => {
        let path: PathPayload;

        switch (processKind?.toLowerCase()) {
          case ProcessType.TRIPABROAD.toLowerCase():
            path = {
              tripAbroad: this.formsService.tripAbroadApi,
            };
            break;
          case ProcessType.TRAVELINPL.toLowerCase():
            path = {
              travelInPL: this.formsService.travelInPlApi,
            };
            break;
          case ProcessType.RESIGNATION.toLowerCase():
            path = {
              resignation: this.formsService.resignationApi,
            };
            break;
          default:
            throw new Error('Unknown process kind');
        }

        const agreements: number[] = [];

        const marketing = this.formsService.agreementsForm.value.marketing as Record<string, boolean>;
        const statements = this.formsService.agreementsForm.value.statements as Record<string, boolean>;

        Object.entries(marketing)
          .filter(([_, value]) => value)
          .forEach(([key]) => {
            const agreementsId = Number(key.replace('agreement.', ''));

            agreements.push(agreementsId);
          });

        Object.entries(statements)
          .filter(([_, value]) => value)
          .forEach(([key]) => {
            const agreementsId = Number(key.replace('agreement.', ''));

            agreements.push(agreementsId);
          });

        const payload: AgentSaveCalculationPayload = {
          path,
          policyholder: this.formsService.policyholderApi,
          insureds: this.formsService.insuredsApi,
          selectedOffert: {
            packageCodeName,
            addOns: selectedAddons ?? {},
          },
          ...this.processService.apiPayload,
          discountCode: {
            code: discountCode || '',
          },
          ...(!(selectedAgentSection.premiumPercentIncrease === undefined || selectedAgentSection.premiumPercentIncrease === null)
            ? {
                agentSection: selectedAgentSection,
              }
            : {}),
          agreements,
          affiliation: {
            affiliateNumber: this.persistStorageService.getString(PersistStorageKeys.AffiliateNumber, 'session'),
          },
        };

        if (JSON.stringify(savedCalculationPayload) !== JSON.stringify(payload)) {
          this.store.dispatch(fromProcess.actions.saveCalculationStart({ payload }));
        }
      });
  }

  getAttachments(): void {
    combineLatest([this.processKind$, this.hashCode$])
      .pipe(first())
      .subscribe(([processKind, hashCode]) => {
        if (!processKind || !hashCode) {
          this.store.dispatch(fromAttachments.actions.fetchError({ error: null }));

          return;
        }

        this.store.dispatch(fromAttachments.actions.fetchStart({ processKind, hashCode }));
      });
  }

  handleDownloadAll(): void {
    this.trackingService.downloadDocs();

    this.attachmentsGroupData$.subscribe((groups) => {
      groups?.forEach((group) => {
        group.attachments.forEach(({ downloadLink }) => {
          this.store.dispatch(fromAttachments.actions.addDownloadedFile({ url: downloadLink }));
        });
      });
    });
  }

  handleDownload(downloadLink: string): void {
    this.store.dispatch(fromAttachments.actions.addDownloadedFile({ url: downloadLink }));
  }
}
